import * as React from 'react';
import { FixMeLater } from '../../utils';
import { SvgIconProps } from '../SvgIcon';
import {
    ButtonCornerStyle,
    ButtonShape,
    ButtonSize,
    ButtonVariant,
    ButtonColor,
    ResponsiveStyleValue,
    useComponentDefaultProps,
    ButtonIconPosition,
} from '../../theming';
import { ButtonBase, ButtonBaseProps } from '../ButtonBase/ButtonBase';
import { useStyles } from './Button.styles';
import { Loader } from '../Loader/Loader';
import { Styler, StylerProps } from '../Styler/Styler';

export interface ButtonProps extends StylerProps, ButtonBaseProps {
    /**
     * The label which will be displayed on the button.
     */
    children?: React.ReactNode;

    /**
     * The colour of the button.
     * @default primary
     */
    color?: ButtonColor;

    /**
     * The button's corner style. It might be preferable to set this on the theme using default props.
     * @default square
     */
    cornerStyle?: ButtonCornerStyle;

    /**
     * If `true`, the button will span the width of its container.
     */
    fullWidth?: ResponsiveStyleValue<boolean>;

    /**
     * An svg icon component. The icon will be displayed before the label.
     */
    icon?: React.ReactElement<SvgIconProps>;

    /**
     * An optional element which will be displayed after the label.
     */
    decoration?: React.ReactElement<any>;

    /**
     * The size of the button.
     * Use object syntax to provide responsive values. The value will apply to the given breakpoint and up, unless overridden.
     * @default m
     */
    size?: ResponsiveStyleValue<ButtonSize>;

    /**
     * The style variant of the button.
     * @default filled
     */
    variant?: ButtonVariant;

    /**
     * The shape of the component.
     * `square` and `round` buttons should have an icon. If text is provided then this
     * will be applied invisibly as an aria-label.
     * @default  default
     */
    shape?: ButtonShape;

    /**
     * If `true`, the button will enter a loading state.
     * @default  false
     */
    loading?: boolean;

    /**
     * The position of any provided icon.
     * @default start
     */
    iconPosition?: ButtonIconPosition;
}

const defaultProps: Partial<ButtonProps> = {
    color: 'primary',
    variant: 'filled',
    size: 'm',
    shape: 'default',
    cornerStyle: 'square',
    iconPosition: 'start',
};

/**
 * Buttons allow users to perform actions and make choices. They communicate actions which the user can take and are typically placed throughout the UI.
 * Use `filled` buttons for high-emphasis actions and `outlined` buttons for medium-emphasis actions.
 */
export const Button: React.ForwardRefExoticComponent<ButtonProps & React.RefAttributes<HTMLElement>> = React.forwardRef(
    function Button(props: ButtonProps, ref: React.ForwardedRef<FixMeLater>): React.JSX.Element {
        const {
            color = 'primary',
            cornerStyle = 'square',
            children: label,
            fullWidth = false,
            icon,
            decoration,
            size = 'm',
            variant = 'filled',
            shape = 'default',
            disabled = false,
            loading = false,
            iconPosition = 'start',
            ...other
        } = useComponentDefaultProps('Button', defaultProps, props);

        const { styles } = useStyles({
            size,
            cornerStyle,
            fullWidth,
            color,
            variant,
            shape,
        });

        const squareOrRound = shape !== 'default';
        const loader = <Loader />;

        const children = [];
        if (!squareOrRound && label) {
            children.push(<span key="label">{label}</span>);
        }
        if (loading) {
            children.unshift(
                React.cloneElement(loader, {
                    css: styles.loader,
                    'data-loader': 'true',
                    key: 'loader',
                })
            );
        } else if (icon) {
            children[iconPosition === 'start' ? 'unshift' : 'push'](
                React.cloneElement(icon, {
                    css: styles.icon,
                    'data-icon': 'true',
                    key: 'icon',
                })
            );
        }

        if (decoration) {
            children.push(
                React.cloneElement(decoration, {
                    'data-decoration': 'true',
                    key: 'decoration',
                })
            );
        }

        return (
            <Styler
                ref={ref}
                component={ButtonBase}
                baseStyles={styles.root}
                aria-label={squareOrRound && label ? label : undefined}
                data-loading={loading || undefined}
                disabled={disabled || loading}
                {...other}
            >
                {children}
            </Styler>
        );
    }
);
